import type { CellContext } from '@tanstack/react-table'
import { ChevronDownIcon } from 'lucide-react'
import pluralize from 'pluralize'

import {
  AccountTypeBadge,
  type AccountTypeBadgeVariant,
} from '@/modules/projects'
import { STATEMENT_STATUSES } from '@/modules/projects/constants'
import type { Statement, StatementSegment } from '@/modules/projects/stores'
import { cn } from '@/utils'
import { formatAccountType } from '../../../labels'
import { pageSpanLabel } from './coverage'

const distinctKinds = (segments: StatementSegment[]) => [
  ...new Set(segments.map((segment) => segment.kind).filter(Boolean)),
]

const problemChip = (segments: StatementSegment[]) => {
  const failed = segments.filter(
    (segment) => segment.status === STATEMENT_STATUSES.failed,
  ).length
  if (failed > 0) return { label: `${failed} failed`, tone: 'failed' as const }

  const notStarted = segments.filter(
    (segment) => segment.status === STATEMENT_STATUSES.notStarted,
  ).length
  if (notStarted > 0) {
    return { label: `${notStarted} not started`, tone: 'pending' as const }
  }
  return null
}

const excludedChipLabel = (statement: Statement) => {
  const ranges = statement.excludedRanges ?? []
  if (ranges.length === 0) return null
  if (ranges.length === 1) {
    const [range] = ranges
    return `${pageSpanLabel(range.startPage, range.endPage).toLowerCase()} set aside`
  }
  return `${ranges.length} ranges set aside`
}

export const ContentsCell = (cell: CellContext<Statement, unknown>) => {
  const statement = cell.row.original
  const segments = statement.segments ?? []
  const kinds = distinctKinds(segments)
  const problem = problemChip(segments)
  const excluded = excludedChipLabel(statement)
  const isExpandable = cell.row.getCanExpand()
  const isExpanded = cell.row.getIsExpanded()

  if (segments.length === 0 && !excluded) {
    return <span className="text-muted-foreground select-none">—</span>
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      {isExpandable && (
        <ChevronDownIcon
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform',
            !isExpanded && '-rotate-90',
          )}
        />
      )}
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {segments.length > 0 && (
          <span className="whitespace-nowrap text-sm text-foreground">
            {segments.length} {pluralize('statement', segments.length)}
          </span>
        )}
        {kinds.map((kind) => (
          <AccountTypeBadge
            key={kind}
            type={formatAccountType(kind) as AccountTypeBadgeVariant}
            size="xs"
          />
        ))}
        {problem && (
          <span
            className={cn(
              'whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
              problem.tone === 'failed'
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-amber-200 bg-amber-50 text-amber-700',
            )}
          >
            {problem.label}
          </span>
        )}
        {excluded && (
          <span className="whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {excluded}
          </span>
        )}
      </div>
    </div>
  )
}
